import { useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  if (n == null) return '-';
  return new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

export function BrosBookingInvoiceLinkagePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [year, setYear] = useState(2026);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosBookingInvoiceLinkage({ year });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">BR OS Booking-Invoice Linkage</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Per-month comparison: booking count, count with/without linked invoices,
          booking value vs invoiced value. Uses created_at for bookings and
          issue_date for invoices.
        </p>

        <div className="flex items-end gap-3">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="border rounded px-3 py-1.5 text-sm bg-background"
            >
              {[2024, 2025, 2026].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </div>

        {result && (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <span>Total bookings: <strong>{result.total_bookings}</strong></span>
              <span>Total invoices: <strong>{result.total_invoices}</strong></span>
            </div>

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-2 py-1.5 font-medium">Month</th>
                    <th className="text-right px-2 py-1.5 font-medium">Bookings</th>
                    <th className="text-right px-2 py-1.5 font-medium">With Invoice</th>
                    <th className="text-right px-2 py-1.5 font-medium">No Invoice</th>
                    <th className="text-right px-2 py-1.5 font-medium" title="Unnormalized: native currency sum, not FX-converted">Booking Value*</th>
                    <th className="text-right px-2 py-1.5 font-medium" title="Unnormalized: native currency sum, not FX-converted">Invoiced (linked)*</th>
                    <th className="text-right px-2 py-1.5 font-medium" title="Unnormalized: native currency sum, not FX-converted">Delta*</th>
                    <th className="text-right px-2 py-1.5 font-medium">Invoices by Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {result.months?.map((m) => {
                    const hasBookings = m.booking_count > 0;
                    const hasDelta = Math.abs(m.delta_booking_vs_invoiced) > 1;
                    return (
                      <tr key={m.month} className={`border-b last:border-0 ${!hasBookings ? 'text-muted-foreground' : ''}`}>
                        <td className="px-2 py-1 font-medium">{m.month}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{m.booking_count}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{m.bookings_with_invoice}</td>
                        <td className="px-2 py-1 text-right tabular-nums">
                          {m.bookings_without_invoice > 0 ? (
                            <span className="text-amber-600">{m.bookings_without_invoice}</span>
                          ) : m.bookings_without_invoice}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums font-mono">{fmt(m.booking_value_sum)}</td>
                        <td className="px-2 py-1 text-right tabular-nums font-mono">{fmt(m.invoiced_linked_to_bookings)}</td>
                        <td className={`px-2 py-1 text-right tabular-nums font-mono ${hasDelta ? 'text-amber-600 font-medium' : ''}`}>
                          {fmt(m.delta_booking_vs_invoiced)}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{m.invoice_count_by_issue_date}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="text-[10px] text-muted-foreground">
              * Value columns are unnormalized: sums are in native currency per entity without FX conversion.
              For normalized (converted) figures, see the BR OS Dashboard billing-vs-sales reconciliation.
            </p>

            {result.divergence_reasons?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground font-medium">
                  Why the two series can legitimately differ ({result.divergence_reasons.length} reasons)
                </summary>
                <ul className="mt-2 space-y-1 pl-4 list-disc text-muted-foreground">
                  {result.divergence_reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
